import { forwardRef } from 'react'
import { Bell } from 'lucide-react'

import { cn } from 'lib/utils'

export interface BellButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	unreadCount: number
	open?: boolean
}

/** "9+" past nine, like the Messages badge beside it. */
export const badgeLabel = (count: number) => (count > 9 ? '9+' : String(count))

/**
 * The bell itself (MYD-542), with the same badge, label and announcement as
 * the Messages button beside it (ChatPopupTrigger), so the two read as a pair.
 * A ref-forwarding button, so a Popover trigger can wrap it.
 */
export const BellButton = forwardRef<HTMLButtonElement, BellButtonProps>(({ unreadCount, open, className, ...props }, ref) => (
	<button
		ref={ref}
		type="button"
		aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
		title="Notifications"
		className={cn(
			'relative inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors',
			'text-gray-500 hover:text-primary',
			'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0080fe] focus-visible:ring-offset-2',
			open && 'bg-gray-100 text-[#0080fe]',
			className,
		)}
		{...props}
	>
		<Bell className="w-5 h-5" />
		{unreadCount > 0 && (
			<span
				aria-hidden="true"
				data-testid="notification-badge"
				className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center ring-2 ring-white"
			>
				{badgeLabel(unreadCount)}
			</span>
		)}
		{/* Announced separately so a new count reaches screen readers without
		    the button re-reading its whole label. */}
		<span className="sr-only" aria-live="polite">
			{unreadCount > 0 ? `${unreadCount} unread notifications` : ''}
		</span>
	</button>
))
BellButton.displayName = 'BellButton'

export default BellButton
